import { DOCUMENT } from "@angular/common";
import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { AppConfig } from "app/config/app.config";
import { DOMAIN_THEMES, DOMAIN_TITLE } from "../constants";
import { DomainTheme, DomainTitle, ToggleTheme } from "../enums";
import { LocalStorageService } from "./local-storage.service";
import { Title } from "@angular/platform-browser";

@Injectable({
  providedIn: "root",
})
export class ThemeService {
  private _localStorageService = inject(LocalStorageService);
  private _document = inject(DOCUMENT);
  private _title = inject(Title);

  private _toggleTheme = signal<ToggleTheme>(ToggleTheme.DEFAULT);
  public toggleTheme = computed(() => this._toggleTheme());

  private _isDarkTheme = signal<boolean>(false);
  public isDarkTheme = computed(() => this._isDarkTheme());

  effectTheme = effect(
    () => {
      this._isDarkTheme.set(this.toggleTheme() == ToggleTheme.DARK);
    },
    { allowSignalWrites: true },
  );

  constructor() {
    this.ensureTheme();
  }

  handleToggleTheme() {
    this._toggleTheme.set(
      this._isDarkTheme() ? ToggleTheme.LIGHT : ToggleTheme.DARK,
    );

    const currentTheme = this.chosseDomainTheme();
    this.applyTheme(currentTheme);
  }

  applyTheme(nameTheme: DomainTheme) {
    // Solo se tocan las clases de tema: pisar body.className borraba también
    // "fuse-splash-screen-hidden" (y el os-name de Fuse), y sin esa clase el
    // CSS del splash deja el body con overflow: hidden -> la página no scrollea.
    this.removeThemeClasses();
    this._document.body.classList.add(
      "mat-app-background",
      nameTheme,
      this.toggleTheme(),
    );
  }

  ensureTheme() {
    const currentTitle = this.chosseDomainTitle();
    this._title.setTitle(currentTitle);

    const currentTheme = this.chosseDomainTheme();
    this.applyTheme(currentTheme);
  }

  chosseDomainTheme(): DomainTheme {
    const hostname = window.location.host;

    const item = DOMAIN_THEMES.find((item) => hostname.includes(item.key));

    return item?.theme || DomainTheme.DEFAULT;
  }

  chosseDomainTitle(): DomainTitle {
    const hostname = window.location.host;

    const item = DOMAIN_TITLE.find((item) => hostname.includes(item.key));

    return item?.title || DomainTitle.DEFAULT;
  }

  reset() {
    this.removeThemeClasses();
    this._document.body.classList.add("mat-app-background");
    this._localStorageService.remove(AppConfig.keyTheme);
  }

  private removeThemeClasses() {
    this._document.body.classList.remove(
      ...Object.values(DomainTheme),
      ...Object.values(ToggleTheme),
    );
  }
}
